/**
 * This file performs various transformations on provided images or image serving URLs.
 *
 * Usage:
 * In Typescript
 *    private transformation: ImageTransformation = new ImageTransformation()
 *        .crop()
 *        .flipHorizontally(); // Can chain transformation functions indefinitely.
 *    private sizes: number[] = [128, 256, 512];
 *    private url: string = 'https://someurl.com';
 *
 * In HTML
 *    <img [srcset]="ImageTransformationService.getSrcSetForImage(url, sizes, transformation)" />
 */
import { Injectable } from '@angular/core';
import { ImageTransformation } from './image-transformation';

export const SERVING_URL_DOMAINS: string[] = ['lh3.googleusercontent.com'];

@Injectable()
export class ImageTransformationService {
  /**
   * Determines whether the given URL is a Google Serving URL.
   * @param url The URL to check.
   * @returns True if it is a Google Serving URL, otherwise False.
   */
  public isServingUrl(url: string): boolean {
    if (url) {
      for (const s of SERVING_URL_DOMAINS) {
        const protocolIndex = url.indexOf('://');
        const domain = protocolIndex > -1 ? url.substring(protocolIndex + 3) : url;
        if (domain.startsWith(s)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Generates and returns a new URL that applies the specified transformation(s) to the provided image. If the image
   * is not a Google Serving URL, it is returned unchanged.
   * @param baseUrl The URL of the image to apply transformations to.
   * @param transformation An object containing all of the transformation flags to apply.
   */
  public getTransformedImage(baseUrl: string, transformation: ImageTransformation): string {
    if (!baseUrl) {
      return null;
    }
    const isServingUrl: boolean = this.isServingUrl(baseUrl);
    if (!isServingUrl || !transformation) {
      return baseUrl;
    }
    const transformationString = transformation.hasFlags() ? `=${transformation.getFlags()}` : '';
    return `${baseUrl}${transformationString}`;
  }

  /**
   * Creates a string of URLs to use as srcset options. One option will be created for each size provided with each option
   * containing all of the additional flags specified by the ImageTransformation object. If no sizes are provided or if the
   * baseUrl is not a Google Serving URL, the original baseUrl is returned unchanged.
   * @param baseUrl The base URL of the image.
   * @param sizes An array of widths in pixels the image is to be made available in.
   * @param transformation An optional object containing a set of additional transformation flags to set.
   *    Omit or use null to display the images with no transformations.
   */
  public getSrcSetForImage(
    baseUrl: string,
    sizes: number[] = null,
    transformation: ImageTransformation = null,
  ): string {
    if (!baseUrl) {
      return null;
    }

    const isServingUrl: boolean = this.isServingUrl(baseUrl);
    if (!isServingUrl || !sizes || sizes.length === 0) {
      return baseUrl;
    }

    let srcset = '';
    const transformationString = transformation ? transformation.getFlags() : '';
    sizes.forEach((size) => {
      if (srcset.length > 0) {
        srcset += ', ';
      }
      srcset += `${baseUrl}=s${size}${transformationString} ${size}w`;
    });
    return srcset;
  }
}
